import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, Button, Paper, Avatar, Chip, IconButton, Switch,
  TextField, Alert, CircularProgress, Tooltip, InputAdornment,
  InputBase, Stack, Collapse, Divider,
} from '@mui/material';
import {
  Add, Edit, Delete, DragIndicator, LocalFireDepartment, NotificationsActive,
  Check, Close, Refresh, Search, East, Whatshot,
} from '@mui/icons-material';
import { io } from 'socket.io-client';
import api, {
  fetchDealsOfDay, createDeal, updateDeal, deleteDeal,
  reorderDeals, notifyDeal, fetchProducts,
} from '../../api/api';

const BRAND = '#667eea';
const BRAND_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

const kes = (n) => `KES ${Number(n).toLocaleString()}`;
const discountPct = (orig, deal) => {
  if (!orig || !deal || deal >= orig) return null;
  return Math.round((1 - deal / orig) * 100);
};

export default function DealsOfDay() {
  const [deals, setDeals] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Edit state
  const [editId, setEditId] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);

  // Add panel state
  const [showAdd, setShowAdd] = useState(false);
  const [addProduct, setAddProduct] = useState(null);
  const [addPrice, setAddPrice] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [addSaving, setAddSaving] = useState(false);

  // Drag state
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  // Suppress socket reloads triggered by this session's own writes
  const suppressSocket = useRef(0);
  const skipNext = () => { suppressSocket.current += 1; };

  const [snack, setSnack] = useState({ show: false, msg: '', isError: false });

  const flash = useCallback((msg, isError = false) => {
    setSnack({ show: true, msg, isError });
    setTimeout(() => setSnack(s => ({ ...s, show: false })), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dRes, pRes] = await Promise.allSettled([fetchDealsOfDay(), fetchProducts()]);
      if (dRes.status === 'fulfilled') {
        const raw = dRes.value.data;
        setDeals(raw?.deals || (Array.isArray(raw) ? raw : []));
      }
      if (pRes.status === 'fulfilled') {
        const raw = pRes.value.data;
        const arr = Array.isArray(raw) ? raw : (raw?.data ?? raw?.products ?? []);
        setProducts(arr.map(p => ({ ...p, name: p.name || p.title || '' })));
      }
    } catch {
      setError('Failed to load deals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live updates — refresh when any admin session changes deals
  useEffect(() => {
    const socket = io(api.defaults.baseURL, {
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });
    const onDealsUpdated = () => {
      if (suppressSocket.current > 0) { suppressSocket.current -= 1; return; }
      load();
    };
    socket.on('deals:updated', onDealsUpdated);
    return () => {
      socket.off('deals:updated', onDealsUpdated);
      socket.disconnect();
    };
  }, [load]);

  /* ── Drag handlers ── */
  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverIndex !== index) setHoverIndex(index);
  };

  // Only clear hover when the cursor leaves the card entirely (not a child element)
  const handleDragLeave = (e) => {
    try {
      if (!e.currentTarget.contains(e.relatedTarget)) setHoverIndex(null);
    } catch {
      setHoverIndex(null);
    }
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    const from = dragIndex;
    setDragIndex(null);
    setHoverIndex(null);
    if (from === null || from === index) return;
    const arr = [...deals];
    const [moved] = arr.splice(from, 1);
    arr.splice(index, 0, moved);
    setDeals(arr);
    skipNext();
    reorderDeals(arr.map((d, i) => ({ id: d._id, position: i + 1 }))).catch(() => {
      flash('Reorder failed', true);
      load();
    });
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setHoverIndex(null);
  };

  /* ── Toggle active ── */
  const handleToggleActive = async (deal) => {
    const prev = deal.active !== false;
    setDeals(ds => ds.map(d => d._id === deal._id ? { ...d, active: !prev } : d));
    try {
      skipNext();
      await updateDeal(deal._id, { active: !prev });
    } catch {
      setDeals(ds => ds.map(d => d._id === deal._id ? { ...d, active: prev } : d));
      flash('Update failed', true);
    }
  };

  /* ── Save edit ── */
  const handleEditSave = async () => {
    setSaving(true);
    try {
      const payload = {
        active: editActive,
        ...(editPrice !== '' ? { dealPrice: Number(editPrice) } : { dealPrice: null }),
      };
      skipNext();
      await updateDeal(editId, payload);
      setDeals(ds => ds.map(d => d._id === editId
        ? { ...d, active: editActive, dealPrice: editPrice !== '' ? Number(editPrice) : null }
        : d
      ));
      setEditId(null);
      flash('Deal updated');
    } catch {
      flash('Update failed', true);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (deal) => {
    setShowAdd(false);
    setEditId(deal._id);
    setEditPrice(deal.dealPrice != null ? String(deal.dealPrice) : '');
    setEditActive(deal.active !== false);
  };

  /* ── Add deal ── */
  const handleAddSave = async () => {
    if (!addProduct) return flash('Select a product first', true);
    setAddSaving(true);
    try {
      skipNext();
      await createDeal({
        productId: addProduct._id,
        position: deals.length + 1,
        ...(addPrice !== '' && { dealPrice: Number(addPrice) }),
      });
      setShowAdd(false);
      setAddProduct(null);
      setAddPrice('');
      setProductQuery('');
      flash('Deal added');
      load();
    } catch (e) {
      flash(e.response?.data?.message || 'Failed to add deal', true);
    } finally {
      setAddSaving(false);
    }
  };

  const closeAdd = () => {
    setShowAdd(false);
    setAddProduct(null);
    setAddPrice('');
    setProductQuery('');
  };

  /* ── Delete ── */
  const handleDelete = async (id) => {
    if (!window.confirm('Remove this deal of the day?')) return;
    try {
      skipNext();
      await deleteDeal(id);
      setDeals(ds => ds.filter(d => d._id !== id));
      flash('Deal removed');
    } catch {
      flash('Delete failed', true);
    }
  };

  /* ── Notify ── */
  const handleNotify = async (id) => {
    if (!window.confirm('Send a push notification to all users for this deal?')) return;
    try {
      await notifyDeal(id);
      flash('Notification sent!');
    } catch {
      flash('Failed to send notification', true);
    }
  };

  const usedIds = useMemo(() => new Set(deals.map(d => d.product?._id || d.productId)), [deals]);

  const productSuggestions = useMemo(() => {
    const q = productQuery.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter(p => !usedIds.has(p._id))
      .filter(p =>
        (p.title || p.name || '').toLowerCase().includes(q) ||
        (p.category?.name || '').toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [products, productQuery, usedIds]);

  const activeCount = deals.filter(d => d.active !== false).length;

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <Box sx={{
              width: 36, height: 36, borderRadius: 2,
              background: BRAND_GRADIENT,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Whatshot sx={{ color: '#fff', fontSize: 20 }} />
            </Box>
            <Typography variant="h5" fontWeight={800}>Deals of the Day</Typography>
          </Stack>
          <Stack direction="row" gap={1.5} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {deals.length} deal{deals.length !== 1 ? 's' : ''} configured
            </Typography>
            {activeCount > 0 && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Typography variant="body2" color="success.main" fontWeight={600}>
                  {activeCount} live
                </Typography>
              </>
            )}
            {deals.length > 1 && (
              <>
                <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
                <Typography variant="body2" color="text.disabled" sx={{ fontSize: 12 }}>
                  drag to reorder
                </Typography>
              </>
            )}
          </Stack>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small" sx={{ borderRadius: 1.5 }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant={showAdd ? 'outlined' : 'contained'}
            startIcon={showAdd ? <Close /> : <Add />}
            onClick={() => { setShowAdd(v => !v); if (showAdd) closeAdd(); setEditId(null); }}
            sx={{
              background: showAdd ? undefined : BRAND_GRADIENT,
              border: showAdd ? `1px solid ${BRAND}` : undefined,
              color: showAdd ? BRAND : undefined,
              fontWeight: 700, borderRadius: 2, textTransform: 'none',
            }}
          >
            {showAdd ? 'Cancel' : 'Add Deal'}
          </Button>
        </Stack>
      </Box>

      {/* ── Snackbar ── */}
      <Collapse in={snack.show}>
        <Alert
          severity={snack.isError ? 'error' : 'success'}
          onClose={() => setSnack(s => ({ ...s, show: false }))}
          sx={{ mb: 2, borderRadius: 2 }}
        >
          {snack.msg}
        </Alert>
      </Collapse>
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {/* ── Add panel ── */}
      <Collapse in={showAdd} unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            mb: 2.5, p: 2.5, borderRadius: 3,
            border: `1.5px dashed ${BRAND}60`,
            bgcolor: `${BRAND}05`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 2 }}>
            Add product to Deals of the Day
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="flex-start">
            {/* Product search */}
            <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
              {addProduct ? (
                <Paper elevation={0} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                }}>
                  <Avatar variant="rounded" src={addProduct.images?.[0]}
                    sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: 'action.hover', flexShrink: 0 }}>
                    {(addProduct.title || '?').charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>{addProduct.title || addProduct.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{kes(addProduct.price)}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => { setAddProduct(null); setAddPrice(''); setProductQuery(''); }}>
                    <Close sx={{ fontSize: 16 }} />
                  </IconButton>
                </Paper>
              ) : (
                <>
                  <Paper elevation={0} sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    px: 1.5, py: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 2,
                  }}>
                    <Search sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                    <InputBase
                      autoFocus
                      value={productQuery}
                      onChange={e => setProductQuery(e.target.value)}
                      placeholder={`Search ${products.length} products…`}
                      sx={{ flex: 1, fontSize: 14 }}
                    />
                    {productQuery && (
                      <IconButton size="small" onClick={() => setProductQuery('')} sx={{ p: 0.3 }}>
                        <Close sx={{ fontSize: 13 }} />
                      </IconButton>
                    )}
                  </Paper>
                  {productSuggestions.length > 0 && (
                    <Paper elevation={6} sx={{
                      position: 'absolute', top: '110%', left: 0, right: 0,
                      zIndex: 1300, borderRadius: 2, border: '1px solid', borderColor: 'divider',
                      maxHeight: 260, overflowY: 'auto',
                    }}>
                      {productSuggestions.map((p, i) => (
                        <Box key={p._id}>
                          {i > 0 && <Divider />}
                          <Box
                            onClick={() => { setAddProduct(p); setAddPrice(p.price != null ? String(p.price) : ''); setProductQuery(''); }}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1.5,
                              px: 1.5, py: 1.25, cursor: 'pointer',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Avatar variant="rounded" src={p.images?.[0]}
                              sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'action.hover', flexShrink: 0 }}>
                              {(p.title || p.name || '?').charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>{p.title || p.name}</Typography>
                              <Stack direction="row" gap={1} alignItems="center">
                                <Typography variant="caption" color="success.main" fontWeight={700}>{kes(p.price)}</Typography>
                                {p.stock !== undefined && (
                                  <Typography variant="caption" color={p.stock > 0 ? 'text.secondary' : 'error.main'}>
                                    {p.stock > 0 ? `${p.stock} in stock` : 'Out of stock'}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                            <Box sx={{
                              width: 24, height: 24, borderRadius: 1, flexShrink: 0,
                              bgcolor: BRAND, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Add sx={{ fontSize: 14, color: '#fff' }} />
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Paper>
                  )}
                  {productQuery.trim() && productSuggestions.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 0.5 }}>
                      No matching products
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {/* Deal price input */}
            <TextField
              size="small"
              type="number"
              label="Deal price (optional)"
              value={addPrice}
              onChange={e => setAddPrice(e.target.value)}
              placeholder="Leave blank for product price"
              InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }}
              sx={{ width: 200, flexShrink: 0 }}
            />

            {/* Discount preview */}
            {addProduct && addPrice && (
              <Box sx={{ display: 'flex', alignItems: 'center', pt: 1 }}>
                {(() => {
                  const pct = discountPct(addProduct.price, Number(addPrice));
                  return pct != null
                    ? <Chip label={`Save ${pct}%`} color="error" size="small" sx={{ fontWeight: 700 }} />
                    : <Typography variant="caption" color="warning.main" fontWeight={600}>Price ≥ original</Typography>;
                })()}
              </Box>
            )}

            {/* Actions */}
            <Stack direction="row" gap={1} sx={{ pt: 0.25, flexShrink: 0 }}>
              <Button
                variant="contained" startIcon={addSaving ? <CircularProgress size={14} color="inherit" /> : <Check />}
                onClick={handleAddSave} disabled={addSaving || !addProduct}
                sx={{ background: BRAND_GRADIENT, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}
              >
                Add
              </Button>
              <Button variant="text" onClick={closeAdd} sx={{ borderRadius: 2, textTransform: 'none' }}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Collapse>

      {/* ── Loading ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
          <CircularProgress sx={{ color: BRAND }} />
        </Box>
      ) : deals.length === 0 && !showAdd ? (
        /* ── Empty state ── */
        <Paper elevation={0} sx={{
          textAlign: 'center', py: 10, px: 4,
          border: '1px dashed', borderColor: 'divider', borderRadius: 3,
        }}>
          <Box sx={{
            width: 72, height: 72, borderRadius: 4, background: BRAND_GRADIENT,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 2, opacity: 0.2,
          }}>
            <Whatshot sx={{ color: '#fff', fontSize: 36 }} />
          </Box>
          <Typography variant="h6" fontWeight={700} color="text.secondary" gutterBottom>
            No deals configured yet
          </Typography>
          <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
            Add products as today's deals — they'll appear in the app's featured section.
          </Typography>
          <Button
            variant="contained" startIcon={<Add />}
            onClick={() => { setShowAdd(true); setAddProduct(null); setAddPrice(''); setProductQuery(''); }}
            sx={{ background: BRAND_GRADIENT, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}
          >
            Add your first deal
          </Button>
        </Paper>
      ) : (
        /* ── Deal cards ── */
        <Stack gap={1.25}>
          {deals.map((deal, index) => {
            const product = deal.product || products.find(p => p._id === deal.productId) || {};
            const img = product.images?.[0] || product.imageUrl || product.image;
            const origPrice = product.price;
            const isEditing = editId === deal._id;
            const dealPrice = isEditing
              ? (editPrice !== '' ? Number(editPrice) : null)
              : deal.dealPrice;
            const pct = discountPct(origPrice, dealPrice);
            const isActive = isEditing ? editActive : deal.active !== false;

            const isDraggingThis = dragIndex === index;
            const isHover = hoverIndex === index && dragIndex !== null && dragIndex !== index;
            const dropAbove = isHover && index < dragIndex;

            return (
              <Paper
                key={deal._id}
                elevation={isDraggingThis ? 6 : 0}
                draggable={!isEditing}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: isEditing ? BRAND : (isHover ? `${BRAND}50` : 'divider'),
                  bgcolor: isHover ? `${BRAND}07` : 'background.paper',
                  cursor: isEditing ? 'default' : (dragIndex !== null ? 'grabbing' : 'grab'),
                  opacity: isDraggingThis ? 0.3 : 1,
                  // Inset shadow as drop indicator — no layout shift
                  boxShadow: isHover
                    ? (dropAbove
                      ? `inset 0 3px 0 0 ${BRAND}, 0 1px 3px rgba(0,0,0,0.06)`
                      : `inset 0 -3px 0 0 ${BRAND}, 0 1px 3px rgba(0,0,0,0.06)`)
                    : isEditing
                      ? `0 0 0 2px ${BRAND}30, 0 2px 8px rgba(0,0,0,0.08)`
                      : '0 1px 3px rgba(0,0,0,0.04)',
                  transition: 'opacity 0.12s, box-shadow 0.12s, border-color 0.12s, background 0.12s',
                  userSelect: 'none',
                  '&:hover': !isEditing && dragIndex === null
                    ? { borderColor: `${BRAND}50`, boxShadow: `0 2px 8px rgba(0,0,0,0.08)` }
                    : {},
                }}
              >
                {/* Drag handle */}
                <Box sx={{
                  color: isEditing ? 'transparent' : 'text.disabled',
                  cursor: isEditing ? 'default' : 'grab',
                  display: 'flex', alignItems: 'center', flexShrink: 0, px: 0.25,
                  transition: 'color 0.1s',
                  '&:hover': { color: isEditing ? 'transparent' : 'text.secondary' },
                }}>
                  <DragIndicator sx={{ fontSize: 20 }} />
                </Box>

                {/* Position badge */}
                <Box sx={{
                  width: 24, height: 24, borderRadius: 1, flexShrink: 0,
                  bgcolor: 'action.selected',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ fontSize: 11 }}>
                    {index + 1}
                  </Typography>
                </Box>

                {/* Product image */}
                <Avatar
                  variant="rounded" src={img}
                  sx={{ width: 52, height: 52, borderRadius: 1.5, flexShrink: 0, bgcolor: 'action.hover' }}
                >
                  <LocalFireDepartment sx={{ color: 'text.disabled' }} />
                </Avatar>

                {/* Product info */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ lineHeight: 1.3, mb: 0.3 }}>
                    {product.name || product.title || 'Unknown Product'}
                  </Typography>
                  {product.category?.name && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.5 }}>
                      {product.category.name}
                    </Typography>
                  )}
                  <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                    <Typography
                      variant="caption" fontWeight={500}
                      color={dealPrice ? 'text.disabled' : 'text.secondary'}
                      sx={{ textDecoration: dealPrice ? 'line-through' : 'none' }}
                    >
                      {origPrice != null ? kes(origPrice) : '—'}
                    </Typography>
                    {dealPrice && origPrice && (
                      <>
                        <East sx={{ fontSize: 11, color: 'text.disabled' }} />
                        <Typography variant="caption" fontWeight={800} color="error.main">
                          {kes(dealPrice)}
                        </Typography>
                        {pct != null && (
                          <Chip
                            label={`-${pct}%`} size="small" color="error"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        )}
                      </>
                    )}
                  </Stack>
                </Box>

                {/* Inline price edit field */}
                {isEditing && (
                  <TextField
                    size="small" type="number" autoFocus
                    label="Deal price"
                    value={editPrice}
                    onChange={e => setEditPrice(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleEditSave();
                      if (e.key === 'Escape') setEditId(null);
                    }}
                    sx={{ width: 175, flexShrink: 0 }}
                  />
                )}

                {/* Active toggle */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, gap: 0.25 }}>
                  <Switch
                    size="small" color="success"
                    checked={isActive}
                    onChange={isEditing
                      ? (e) => setEditActive(e.target.checked)
                      : () => handleToggleActive(deal)
                    }
                  />
                  <Typography sx={{
                    fontSize: 9, fontWeight: 700, letterSpacing: 0.3,
                    color: isActive ? 'success.main' : 'text.disabled',
                  }}>
                    {isActive ? 'LIVE' : 'OFF'}
                  </Typography>
                </Box>

                {/* Actions */}
                <Stack direction="row" gap={0.25} sx={{ flexShrink: 0, ml: 0.5 }}>
                  {isEditing ? (
                    <>
                      <Tooltip title="Save (Enter)">
                        <IconButton size="small" color="success" onClick={handleEditSave} disabled={saving}>
                          {saving ? <CircularProgress size={14} color="inherit" /> : <Check fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel (Esc)">
                        <IconButton size="small" onClick={() => setEditId(null)}>
                          <Close fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Edit deal price">
                        <IconButton size="small" color="primary" onClick={() => openEdit(deal)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Notify all users">
                        <IconButton size="small" sx={{ color: 'warning.main' }} onClick={() => handleNotify(deal._id)}>
                          <NotificationsActive fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove deal">
                        <IconButton size="small" color="error" onClick={() => handleDelete(deal._id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
